import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatEur, formatHours, formatDate } from "@/lib/api";
import {
  Receipt, FileSearch, Clock, CalendarClock, ChevronRight, ChevronLeft, ArrowUpRight,
  ShoppingCart, AlertCircle, TrendingUp, FolderKanban, Lightbulb, Truck, Hammer, MapPin,
  PauseCircle, PlayCircle, MoreHorizontal, EyeOff, Check,
} from "lucide-react";

const LEVEL_DOT = {
  rouge: "bg-[var(--ppd)]",
  orange: "bg-amber-400",
  vert: "bg-emerald-500",
};
const LEVEL_BORDER = {
  rouge: "border-l-[var(--ppd)]",
  orange: "border-l-amber-300",
  vert: "border-l-emerald-300",
};

const STATUS_LABEL = {
  a_demarrer: "À démarrer", en_cours: "En cours",
  en_attente_client: "Attente client", en_attente_mairie: "Attente mairie",
  livre: "Livré", abandonne: "Abandonné",
};

function KPI({ label, value, hint, accent, to, testid }) {
  const Wrap = to ? Link : "div";
  return (
    <Wrap to={to} className={`card-flat p-4 ${to ? "cursor-pointer hover:border-[var(--ppd)]/40 hover:shadow-sm" : ""}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{label}</div>
      <div className={`kpi-number text-2xl mt-1 ${accent || ""}`}>{value}</div>
      {hint && <div className="text-[11px] text-stone-500 mt-1">{hint}</div>}
    </Wrap>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [actions, setActions] = useState(null);

  const load = async () => {
    const [dash, today] = await Promise.all([api.get("/dashboard"), api.get("/assistant/today")]);
    setD(dash.data);
    setActions(today.data);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/projects/${id}`, { status });
    await load();
  };

  const setPin = async (id, pin) => {
    await api.put(`/projects/${id}`, { urgence_pin: pin });
    await load();
  };

  const toggleCustomStep = async (id, sid) => {
    await api.post(`/projects/${id}/custom-steps/${sid}/toggle`);
    await load();
  };

  if (!d) return <div className="text-sm text-stone-500" data-testid="dashboard-loading">Chargement…</div>;

  const actifs = d.projets_actifs || [];
  const sum = d.alertes_summary || {};
  const today = actions?.items || [];
  const haute = today.filter((i) => i.priority === "haute");

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Pilotage</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">Tableau de bord</h1>
          <p className="text-sm text-stone-600 mt-1">Cliquez sur n'importe quelle carte pour accéder directement aux détails.</p>
        </div>
      </div>

      {/* ===== KPI pilotage ===== */}
      <section data-testid="dash-kpis">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Projets actifs" value={d.production.en_cours + d.production.a_demarrer + (d.production.bloques || 0)}
               accent="text-stone-900" to="/projets" testid="kpi-actifs"/>
          <KPI label="CA réalisé (HT)" value={formatEur(d.finance.ca_realise ?? d.finance.ca_encaisse)} accent="text-[var(--ppd)]"
               hint={`Encaissé net d'avoirs · Impayés : ${formatEur(d.finance?.impaye_ht || 0)} (${d.finance?.nb_impayees || 0})`} to="/finance?status=paye" testid="kpi-ca-realise"/>
          <KPI label="CA prévisionnel (HT)" value={formatEur(d.finance.ca_previsionnel)} accent="text-stone-900"
               hint="Devis validés (hors projets abandonnés)" to="/projets" testid="kpi-ca-previsionnel"/>
          <KPI label="Reste à encaisser (HT)" value={formatEur(d.finance.reste_a_encaisser)} accent="text-amber-700"
               hint="Factures impayées + carnet signé à facturer" to="/finance" testid="kpi-reste-encaisser"/>
        </div>
        {/* Rangée 2 : Marge réelle & Marge prévisionnelle pondérée */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <KPI label="Marge réelle (HT)" value={formatEur(d.rentabilite?.marge_reelle || 0)} accent="text-emerald-700"
               hint={`CA encaissé net − sous-traitance − rétrocommissions${d.finance?.ca_realise ? ` · ${Math.round(((d.rentabilite?.marge_reelle || 0) / d.finance.ca_realise) * 100)} %` : ""}`} testid="kpi-marge-reelle"/>
          <KPI label="Marge prévisionnelle pondérée (HT)" value={formatEur(d.rentabilite?.marge_prev_ponderee || 0)} accent="text-emerald-700"
               hint="CA prévisionnel HT − (temps passé global × 72 €/h)" testid="kpi-marge-ponderee"/>
        </div>
      </section>

      {/* ===== Actions prioritaires & À surveiller (fusionnées) ===== */}
      <section data-testid="dash-actions">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">À faire &amp; à surveiller</div>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-0.5">Actions prioritaires</h2>
          </div>
          <Link to="/assistant" className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1">Vue assistante <ArrowUpRight size={12} /></Link>
        </div>

        {/* Groupes d'alertes projets par niveau — chaque carte est déplaçable via son menu ⋯ */}
        <ManualTasksGroup level="rouge" label="Tâches urgentes (manuelles)" items={sum.taches_urgentes_rouge} onToggle={toggleCustomStep} testid="urgent-taches-rouge"/>
        <AlertGroup level="rouge" label="Démarches administratives à déposer — urgent" catKey="demarches_a_deposer" projets={sum.demarches_a_deposer_projets} testid="urgent-demarches" itemTestid="urgent-demarche" onMove={setPin} onStatus={setStatus}/>
        <ManualTasksGroup level="orange" label="Tâches à faire (manuelles)" items={sum.taches_urgentes_orange} onToggle={toggleCustomStep} testid="urgent-taches-orange"/>
        <AlertGroup level="orange" label="Projet à démarrer (aucun devis)" catKey="projets_a_demarrer" projets={sum.projets_a_demarrer} testid="alert-a-demarrer" itemTestid="alert-demarrer" onMove={setPin} onStatus={setStatus}/>
        <AlertGroup level="orange" label="Avancement à lancer (acompte payé)" catKey="avancement_a_lancer" projets={sum.avancement_a_lancer} testid="alert-avancement" itemTestid="alert-avanc" onMove={setPin} onStatus={setStatus}/>
        <AlertGroup level="orange" label="Facture finale à régler (dossier finalisé)" catKey="facture_finale_a_regler" projets={sum.facture_finale_a_regler} testid="alert-facture-finale" itemTestid="alert-facture" onMove={setPin} onStatus={setStatus}/>
        <AlertGroup level="neutre" label="Relances client à faire (en attente client)" catKey="relances_client" projets={sum.relances_client_projets} testid="alert-relance-client" itemTestid="relance-client" onMove={setPin} onStatus={setStatus}/>

        {(() => {
          const tiles = [
            { key: "impayes", count: sum.factures_jointes_impayees || 0, label: "Factures impayées", icon: AlertCircle, level: "rouge", to: "/projets" },
            { key: "depassement", count: sum.projets_depassement || 0, label: "Dépassement temps", icon: TrendingUp, level: "rouge", to: "/pilotage" },
            { key: "complement", count: sum.demarches_complement || 0, label: "Dossiers à compléter", icon: FileSearch, level: "rouge", to: "/projets" },
            { key: "relances", count: sum.factures_a_relancer || 0, label: "Relances factures", icon: Receipt, level: "ambre", to: "/finance?status=relancer" },
            { key: "echeances", count: sum.echeances_semaine || 0, label: "Échéances 7 jours", icon: CalendarClock, level: "ambre", to: "/projets?tab=en_cours" },
            { key: "editorial", count: (sum.editorial || []).filter((e) => e.level === "rouge").length, label: "Publications en retard", icon: Lightbulb, level: "ambre", to: "/ideas" },
            { key: "fmob", count: (actions?.commandes_fm?.length) || 0, label: "Commandes Franchi'Mob", icon: ShoppingCart, level: "neutre", to: "/franchimob" },
            { key: "publi", count: (actions?.publications?.length) || 0, label: "Publications à planifier", icon: CalendarClock, level: "neutre", to: "/ideas" },
          ].filter((t) => t.count > 0);
          const groups = (sum.demarches_a_deposer_projets || []).length + (sum.projets_a_demarrer || []).length
            + (sum.avancement_a_lancer || []).length + (sum.facture_finale_a_regler || []).length
            + (sum.relances_client_projets || []).length + (sum.taches_urgentes_rouge || []).length + (sum.taches_urgentes_orange || []).length;

          if (tiles.length === 0 && groups === 0) {
            return (
              <div className="card-flat p-6 text-sm text-stone-500 italic" data-testid="dash-actions-empty">
                Rien d'urgent à traiter aujourd'hui. Tout est à jour.
              </div>
            );
          }
          if (tiles.length === 0) return null;
          return (
            <div className="flex flex-wrap gap-3" data-testid="urgency-tiles">
              {tiles.map(({ key, ...t }) => <UrgencyTile key={key} {...t} />)}
            </div>
          );
        })()}

        {haute.length > 0 && (
          <Link to="/assistant" className="mt-4 text-[11px] text-stone-700 bg-[var(--pink-powder)]/40 border border-[var(--pink-powder)] rounded-md px-3 py-2 inline-flex items-center gap-1.5 hover:bg-[var(--pink-powder)]/60">
            <AlertCircle size={12}/> <span><span className="font-semibold">{haute.length}</span> action(s) à priorité haute — ouvrir la vue assistante</span>
          </Link>
        )}
      </section>

      {/* ===== Planning timeline (Gantt) ===== */}
      <GanttTimeline />
    </div>
  );
}

const GROUP_STYLE = {
  rouge: { box: "border-rose-200 bg-rose-50", title: "text-rose-800", badge: "bg-rose-600 text-white", chip: "bg-white border-rose-200 text-rose-700 hover:bg-rose-100 hover:border-rose-300" },
  orange: { box: "border-amber-200 bg-amber-50", title: "text-amber-800", badge: "bg-amber-500 text-white", chip: "bg-white border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-300" },
  neutre: { box: "border-stone-200 bg-stone-50", title: "text-stone-700", badge: "bg-stone-800 text-white", chip: "bg-white border-stone-200 text-stone-700 hover:bg-stone-100 hover:border-stone-400" },
};

const MOVE_CATS = [
  { key: "demarches_a_deposer", label: "Démarches à déposer (rouge)" },
  { key: "projets_a_demarrer", label: "Projet à démarrer (orange)" },
  { key: "avancement_a_lancer", label: "Avancement à lancer (orange)" },
  { key: "facture_finale_a_regler", label: "Facture finale à régler (orange)" },
  { key: "relances_client", label: "Relances client (neutre)" },
];

function ChipCard({ p, styleChip, itemTestid, catKey, onMove, onStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`${itemTestid}-${p.id}`}
      className={`relative text-xs font-semibold border rounded-md pl-2.5 pr-1 py-1 transition flex items-center gap-1.5 ${styleChip}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${p.brand === "PPD" ? "bg-[var(--ppd)]" : "bg-amber-500"}`} />
      <Link to={`/projets/${p.id}`} className="flex items-center gap-1 hover:underline">
        {p.nom} <ChevronRight size={11} />
      </Link>
      <button type="button" title="Déplacer / options" data-testid={`chip-menu-${p.id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        className="ml-0.5 p-1 rounded-md text-stone-400 hover:text-stone-800 hover:bg-black/5 transition-colors">
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.preventDefault(); setOpen(false); }} />
          <div className="absolute right-0 top-full mt-1 z-50 w-60 bg-white border border-stone-200 rounded-lg shadow-lg py-1 text-left" data-testid={`chip-menu-panel-${p.id}`}>
            <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-stone-400 font-bold">Déplacer vers</div>
            {MOVE_CATS.filter((c) => c.key !== catKey).map((c) => (
              <button key={c.key} type="button" data-testid={`chip-move-${c.key}-${p.id}`}
                onClick={(e) => { e.preventDefault(); setOpen(false); onMove(p.id, c.key); }}
                className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-100">{c.label}</button>
            ))}
            <div className="border-t border-stone-100 my-1" />
            <button type="button" data-testid={`chip-move-auto-${p.id}`}
              onClick={(e) => { e.preventDefault(); setOpen(false); onMove(p.id, null); }}
              className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-100">↺ Position automatique</button>
            <button type="button" data-testid={`chip-hide-${p.id}`}
              onClick={(e) => { e.preventDefault(); setOpen(false); onMove(p.id, "masquer"); }}
              className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-100 flex items-center gap-1.5"><EyeOff size={12}/> Masquer des urgences</button>
            <div className="border-t border-stone-100 my-1" />
            <button type="button" data-testid={`chip-standby-${p.id}`}
              onClick={(e) => { e.preventDefault(); setOpen(false); onStatus(p.id, "en_attente_client"); }}
              className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-100 flex items-center gap-1.5"><PauseCircle size={12}/> Mettre en attente client</button>
            <button type="button" data-testid={`chip-resume-${p.id}`}
              onClick={(e) => { e.preventDefault(); setOpen(false); onStatus(p.id, "en_cours"); }}
              className="w-full text-left px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-100 flex items-center gap-1.5"><PlayCircle size={12}/> Reprendre (en cours)</button>
          </div>
        </>
      )}
    </div>
  );
}

function AlertGroup({ level, label, projets, testid, itemTestid, catKey, onMove, onStatus }) {
  const list = projets || [];
  if (list.length === 0) return null;
  const s = GROUP_STYLE[level] || GROUP_STYLE.neutre;
  const Icon = level === "neutre" ? Clock : AlertCircle;
  return (
    <div className={`mb-3 rounded-lg border p-3 ${s.box}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} className={s.title} />
        <span className={`text-sm font-bold ${s.title}`}>{label}</span>
        <span className={`pill text-[10px] ${s.badge}`}>{list.length}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((p) => (
          <ChipCard key={p.id} p={p} styleChip={s.chip} itemTestid={itemTestid} catKey={catKey} onMove={onMove} onStatus={onStatus} />
        ))}
      </div>
    </div>
  );
}

function ManualTasksGroup({ level, label, items, onToggle, testid }) {
  const list = items || [];
  if (list.length === 0) return null;
  const s = GROUP_STYLE[level] || GROUP_STYLE.neutre;
  return (
    <div className={`mb-3 rounded-lg border p-3 ${s.box}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle size={15} className={s.title} />
        <span className={`text-sm font-bold ${s.title}`}>{label}</span>
        <span className={`pill text-[10px] ${s.badge}`}>{list.length}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((t) => (
          <div key={t.step_id} data-testid={`manual-task-${t.step_id}`}
            className={`text-xs font-semibold border rounded-md pl-1 pr-2.5 py-1 transition flex items-center gap-1.5 ${s.chip}`}>
            <button type="button" title="Marquer comme fait" data-testid={`manual-task-done-${t.step_id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(t.id, t.step_id); }}
              className="p-1 rounded-md text-stone-400 hover:text-emerald-700 hover:bg-black/5 transition-colors">
              <Check size={13} />
            </button>
            <Link to={`/projets/${t.id}`} className="flex items-center gap-1 hover:underline">
              <span className="text-stone-500 font-normal">{t.nom} ·</span> {t.label}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

const TILE_STYLE = {
  rouge: { card: "border-rose-200 bg-rose-50 hover:border-rose-300", badge: "bg-rose-600 text-white", num: "text-rose-700" },
  ambre: { card: "border-amber-200 bg-amber-50 hover:border-amber-300", badge: "bg-amber-500 text-white", num: "text-amber-700" },
  neutre: { card: "border-stone-200 bg-white hover:border-stone-400", badge: "bg-stone-900 text-white", num: "text-stone-900" },
};

function UrgencyTile({ count, label, icon: Icon, level, to }) {
  const s = TILE_STYLE[level] || TILE_STYLE.neutre;
  return (
    <Link to={to} data-testid={`urgency-tile-${label}`}
      className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition hover:shadow-sm flex-1 min-w-[168px] ${s.card}`}>
      <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.badge}`}><Icon size={17} /></span>
      <div className="min-w-0">
        <div className={`text-2xl font-bold leading-none ${s.num}`}>{count}</div>
        <div className="text-[11px] text-stone-500 font-semibold mt-1 truncate">{label}</div>
      </div>
    </Link>
  );
}

const CORAL = "#F2603C";
const MONTHS_SHORT = ["Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
const FR_OVERRIDES = [
  [/PMS/, "#7C3AED"],       // PMS = violet
  [/MAAG/, "#1E3A8A"],      // MAAG = bleu foncé
  [/EYES/, "#0D9488"],
  [/EN[EÉ]/, "#DB2777"],
];
const FR_PALETTE = ["#2563EB", "#059669", "#D97706", "#DB2777", "#4F46E5", "#0891B2", "#65A30D", "#9333EA", "#DC2626", "#0D9488", "#7C3AED", "#CA8A04"];
function frColor(name) {
  const up = (name || "").toUpperCase();
  for (const [re, c] of FR_OVERRIDES) { if (re.test(up)) return c; }
  let h = 0; for (let i = 0; i < up.length; i++) h = (h * 31 + up.charCodeAt(i)) >>> 0;
  return FR_PALETTE[h % FR_PALETTE.length];
}

const DAY_MS = 86400000;
function pd(s) { return s ? new Date(s + "T00:00:00") : null; }
function fmtShort(s) { const d = pd(s); return d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}` : ""; }

function GanttTimeline() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [colors, setColors] = useState({});
  const [view, setView] = useState("mois"); // "mois" | "semaine"
  const scrollRef = useRef(null);
  const todayXRef = useRef(0);

  useEffect(() => {
    (async () => {
      const [c, col] = await Promise.all([api.get("/dashboard/calendar"), api.get("/planning/colors")]);
      setProjects(c.data.projects || []);
      setColors(col.data.colors || {});
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, todayXRef.current - 220);
  }, [projects, view]);

  const colorOf = (fr) => colors[fr] || frColor(fr);
  const saveColor = async (fr, color) => {
    setColors((c) => ({ ...c, [fr]: color }));
    try { await api.post("/planning/colors", { franchiseur: fr, color }); } catch (e) { /* noop */ }
  };

  const pxPerDay = view === "mois" ? 6 : 22;

  // Domain: min/max across all dates
  const allDates = [];
  projects.forEach((p) => {
    [p.start, p.end, p.livraison].forEach((d) => d && allDates.push(d));
    (p.demarches || []).forEach((dm) => { if (dm.depot) allDates.push(dm.depot); if (dm.retour) allDates.push(dm.retour); });
  });

  if (projects.length === 0 || allDates.length === 0) {
    return (
      <section data-testid="dash-calendar">
        <GanttHeader view={view} setView={setView} count={0} />
        <div className="card-flat p-8 text-sm text-stone-400 italic text-center">Aucun projet planifié (renseigne les dates de démarrage/objectif).</div>
      </section>
    );
  }

  const minD = pd(allDates.reduce((a, b) => (a < b ? a : b)));
  const maxD = pd(allDates.reduce((a, b) => (a > b ? a : b)));
  const domainStart = new Date(minD.getTime() - 3 * DAY_MS);
  const domainEnd = new Date(maxD.getTime() + 7 * DAY_MS);
  const totalDays = Math.round((domainEnd - domainStart) / DAY_MS) + 1;
  const totalW = totalDays * pxPerDay;
  const x = (s) => { const d = pd(s); return d ? Math.round((d - domainStart) / DAY_MS) * pxPerDay : 0; };
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayX = (pd(todayStr) >= domainStart && pd(todayStr) <= domainEnd) ? x(todayStr) : null;
  todayXRef.current = todayX != null ? todayX : (x(todayStr) > 0 ? x(todayStr) : 0);

  // Header segments (months or weeks)
  const segs = [];
  if (view === "mois") {
    let cur = new Date(domainStart.getFullYear(), domainStart.getMonth(), 1);
    while (cur <= domainEnd) {
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const segStart = cur < domainStart ? domainStart : cur;
      const segEnd = next < domainEnd ? next : new Date(domainEnd.getTime() + DAY_MS);
      const left = Math.round((segStart - domainStart) / DAY_MS) * pxPerDay;
      const width = Math.round((segEnd - segStart) / DAY_MS) * pxPerDay;
      segs.push({ left, width, label: `${MONTHS_SHORT[cur.getMonth()]} ${cur.getFullYear()}` });
      cur = next;
    }
  } else {
    // weeks starting Monday
    let cur = new Date(domainStart);
    const off = (cur.getDay() + 6) % 7; cur = new Date(cur.getTime() - off * DAY_MS);
    while (cur <= domainEnd) {
      const next = new Date(cur.getTime() + 7 * DAY_MS);
      const segStart = cur < domainStart ? domainStart : cur;
      const segEnd = next < domainEnd ? next : new Date(domainEnd.getTime() + DAY_MS);
      const left = Math.round((segStart - domainStart) / DAY_MS) * pxPerDay;
      const width = Math.round((segEnd - segStart) / DAY_MS) * pxPerDay;
      segs.push({ left, width, label: `sem. ${String(cur.getDate()).padStart(2, "0")}/${String(cur.getMonth() + 1).padStart(2, "0")}` });
      cur = next;
    }
  }

  const LABEL_W = 176;

  return (
    <section data-testid="dash-calendar">
      <GanttHeader view={view} setView={setView} count={projects.length} />

      {/* Legend franchiseurs (couleur personnalisable) */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3 text-[11px] text-stone-600 items-center">
        {Array.from(new Set(projects.map((p) => p.franchiseur))).slice(0, 12).map((fr) => (
          <label key={fr} className="flex items-center gap-1.5 cursor-pointer group/leg" title="Cliquer pour changer la couleur">
            <span className="relative inline-block w-3.5 h-3.5 rounded ring-1 ring-black/5 group-hover/leg:ring-2 group-hover/leg:ring-stone-400" style={{ background: colorOf(fr) }}>
              <input type="color" value={colorOf(fr)} onChange={(e) => saveColor(fr, e.target.value)} data-testid={`color-picker-${fr}`} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            </span>
            {fr}
          </label>
        ))}
        <span className="flex items-center gap-1.5"><MapPin size={12} style={{ color: CORAL }} /> Date clé (dépôt / retour mairie / livraison)</span>
        <span className="text-stone-400 text-[10px] italic">· clic sur une pastille = changer la couleur</span>
      </div>

      <div ref={scrollRef} className="card-flat overflow-x-auto" data-testid="gantt-scroll">
        <div style={{ minWidth: LABEL_W + totalW }}>
          {/* Header axis */}
          <div className="flex border-b border-stone-200 bg-stone-50 sticky top-0 z-20">
            <div className="shrink-0 sticky left-0 z-30 bg-stone-50 border-r border-stone-200 px-3 py-2 text-[10px] uppercase tracking-widest text-stone-500 font-bold" style={{ width: LABEL_W }}>Projet</div>
            <div className="relative" style={{ width: totalW, height: 32 }}>
              {segs.map((s) => (
                <div key={`${s.label}-${s.left}`} className="absolute top-0 bottom-0 border-l border-stone-200 px-1.5 py-2 text-[10px] font-semibold text-stone-500 truncate" style={{ left: s.left, width: s.width }}>{s.label}</div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="relative">
            {/* gridlines overlay */}
            <div className="absolute pointer-events-none" style={{ left: LABEL_W, top: 0, bottom: 0, width: totalW }}>
              {segs.map((s) => <div key={`grid-${s.left}`} className="absolute top-0 bottom-0 border-l border-stone-100" style={{ left: s.left }} />)}
              {todayX !== null && <div className="absolute top-0 bottom-0 border-l-2 border-dashed" style={{ left: todayX, borderColor: CORAL }} />}
            </div>

            {projects.map((p) => {
              const color = colorOf(p.franchiseur);
              const bx = x(p.start);
              const bw = Math.max(x(p.end) - bx + pxPerDay, 26);
              const pins = [];
              if (p.livraison) pins.push({ at: p.livraison, label: `Livraison · ${fmtShort(p.livraison)}` });
              (p.demarches || []).forEach((dm) => {
                if (dm.depot) pins.push({ at: dm.depot, label: `${dm.type} · Dépôt ${fmtShort(dm.depot)}` });
                if (dm.retour) pins.push({ at: dm.retour, label: `${dm.type} · Retour mairie ${fmtShort(dm.retour)}` });
              });
              return (
                <div key={p.id} className="flex border-b border-stone-100 hover:bg-stone-50/60 group" data-testid={`gantt-row-${p.id}`}>
                  <button onClick={() => navigate(`/projets/${p.id}`)} className="shrink-0 sticky left-0 z-10 bg-white group-hover:bg-stone-50 border-r border-stone-200 px-3 py-2 text-left" style={{ width: LABEL_W }}>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                      <span className="text-xs font-semibold text-stone-900 truncate">{p.nom}</span>
                    </div>
                    <div className="text-[10px] text-stone-400 truncate pl-4">{p.franchiseur}</div>
                  </button>
                  <div className="relative" style={{ width: totalW, height: 46 }}>
                    {/* duration bar */}
                    <button onClick={() => navigate(`/projets/${p.id}`)} title={p.undated ? `${p.nom} · dates à renseigner` : `${p.nom} · ${fmtShort(p.start)} → ${fmtShort(p.end)}`}
                      className={`absolute rounded-md flex items-center px-2 text-[10px] font-semibold truncate hover:opacity-90 ${p.undated ? "text-stone-500 border border-dashed" : "text-white shadow-sm"}`}
                      style={p.undated
                        ? { left: bx, width: Math.max(bw, 90), top: 22, height: 18, background: "repeating-linear-gradient(45deg,#F1F0EE,#F1F0EE 4px,#E7E5E4 4px,#E7E5E4 8px)", borderColor: color }
                        : { left: bx, width: bw, top: 22, height: 18, background: color }}>
                      <span className="truncate">{p.undated ? "dates ?" : (bw > 60 ? p.nom : "")}</span>
                    </button>
                    {/* pins (dates clés) */}
                    {pins.map((pin) => (
                      <div key={`${pin.label}-${pin.at}`} className="absolute flex flex-col items-center -translate-x-1/2" style={{ left: x(pin.at), top: 2 }} title={pin.label}>
                        <MapPin size={13} style={{ color: CORAL }} fill={CORAL} className="opacity-90" />
                        <span className="w-px h-3" style={{ background: CORAL }} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function GanttHeader({ view, setView, count }) {
  return (
    <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Planning</div>
        <h2 className="font-display text-xl font-semibold tracking-tight mt-0.5">Timeline · production &amp; démarches {count > 0 && <span className="text-stone-400 font-normal text-sm">· {count} projets</span>}</h2>
      </div>
      <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5" data-testid="gantt-view-toggle">
        <button data-testid="gantt-view-mois" onClick={() => setView("mois")} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${view === "mois" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>Mois</button>
        <button data-testid="gantt-view-semaine" onClick={() => setView("semaine")} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${view === "semaine" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>Semaine</button>
      </div>
    </div>
  );
}

function AlertLink({ to, count, label, testid, level }) {
  if (!count) return null;
  const dot = level === "rouge" ? "bg-[var(--ppd)]" : "bg-amber-500";
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-2 hover:text-stone-900 text-stone-700">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`}/>
      <span><span className="font-semibold">{count}</span> {label}</span>
    </Link>
  );
}

function ActionCard({ icon: Icon, title, items, renderItem, emptyTo, allTo, count, testid }) {
  const navigate = useNavigate();
  return (
    <div onClick={() => navigate(allTo)} role="link" tabIndex={0} className="card-flat p-4 hover:border-[var(--ppd)]/40 hover:shadow-sm flex flex-col cursor-pointer" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-stone-700" />
        <h4 className="text-[10px] uppercase tracking-widest font-bold text-stone-600 flex-1">{title}</h4>
        {count > 0 && <span className="pill bg-[var(--pink-powder)] text-stone-800 text-[10px]">{count}</span>}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-stone-400 italic flex-1">Aucune.</div>
      ) : (
        <div className="flex-1">
          {items.map((it, idx) => {
            const r = renderItem(it);
            return (
              <button type="button" key={it.id || idx} onClick={(e) => { e.stopPropagation(); navigate(r.to); }} className="block w-full text-left text-xs py-1 border-b border-stone-50 last:border-0 hover:bg-stone-50 -mx-1 px-1 rounded">
                <div className="font-semibold text-stone-900 truncate">{r.primary}</div>
                <div className="text-stone-500 truncate">{r.secondary}</div>
              </button>
            );
          })}
        </div>
      )}
      <div className="text-[10px] text-stone-400 mt-2 flex items-center gap-1 group-hover:text-stone-700">
        Voir tous <ChevronRight size={10}/>
      </div>
    </div>
  );
}

